/**
 * Workflow Field Definition Types
 *
 * TypeScript types for the standardized input/output field management system.
 * These types mirror the Python schemas in api/schemas/workflow_fields.py
 */

/**
 * Supported field types for workflow node I/O
 *
 * Metadata types: values stored directly
 * Media types: value is file path, actual file stored separately
 */
export type FieldType =
  // Metadata types
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  // Media types (value is file path)
  | 'image'      // Image file (png, jpeg, webp, gif, svg)
  | 'audio'      // Audio file (mp3, wav, m4a, ogg)
  | 'video'      // Video file (mp4, webm)
  | 'document'   // Document file (pdf, markdown, plain text)
  // Legacy (deprecated, use 'image' instead)
  | 'image_url';

/**
 * UI control types for config field rendering
 */
export type ConfigFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'checkbox'
  | 'radio'
  | 'slider'
  | 'custom';

/**
 * Object key info for selecting sub-keys from object-type outputs
 * (Defined before FieldDefinition for proper type resolution)
 */
export interface ObjectKeyInfo {
  /** Key name */
  key: string;
  /** Field type of this key */
  type: FieldType;
  /** Display label */
  label?: string;
  /** Description of what this key contains */
  description?: string;
}

/**
 * Definition of a single input or output field
 */
export interface FieldDefinition {
  /** Field key (e.g., 'prompt', 'image_1') */
  key: string;
  /** Field data type */
  type: FieldType;
  /** Display label for UI */
  label?: string;
  /** Field description */
  description?: string;
  /** Whether this field is required */
  required: boolean;
  /** Default value if not provided */
  default?: unknown;
  /** Whether this field supports indexing (e.g., image_1, image_2) */
  indexed?: boolean;
  /** Max connections for indexed fields */
  max_connections?: number;
  /** For object-type fields: available sub-keys that can be extracted */
  object_keys?: ObjectKeyInfo[];
}

/**
 * Output field definition with optional OutputSchema reference
 */
export interface OutputFieldDefinition extends FieldDefinition {
  /** Reference to centralized OutputSchema for structured outputs */
  output_schema_key?: string;
  /** Reference to input field that provides the value for this output */
  value_source_input_key?: string;
}

/**
 * Special source node key for literal/fixed values
 */
export const LITERAL_SOURCE_KEY = '_literal_';

/**
 * Prefix for config option mappings to distinguish from input field mappings.
 *
 * Scope Design:
 * - Input Fields Section: Maps upstream node outputs → current node's input variables
 *   - Key: original field key (e.g., 'title', 'body')
 *   - Source: upstream node key (e.g., 'lucky_charm')
 *
 * - Config Options Section: References current node's input variables or global variables
 *   - Key: prefixed with CONFIG_MAPPING_PREFIX (e.g., '__config__title')
 *   - Source: 'input' (custom input), 'ctx' (context), 'var' (start variables)
 *
 * This prefix ensures the two sections' mappings never collide.
 */
export const CONFIG_MAPPING_PREFIX = '__config__';

/**
 * Check if a key is a config mapping key
 */
export function isConfigMappingKey(key: string): boolean {
  return key.startsWith(CONFIG_MAPPING_PREFIX);
}

/**
 * Convert a field key to a config mapping key (idempotent).
 * Safe to call multiple times - won't add duplicate prefix.
 */
export function toConfigMappingKey(fieldKey: string): string {
  if (fieldKey.startsWith(CONFIG_MAPPING_PREFIX)) {
    return fieldKey;  // Already has prefix
  }
  return `${CONFIG_MAPPING_PREFIX}${fieldKey}`;
}

/**
 * Extract the original field key from a config mapping key
 */
export function fromConfigMappingKey(configKey: string): string {
  return configKey.replace(CONFIG_MAPPING_PREFIX, '');
}

/**
 * Mapping from an upstream output to an input field
 */
export interface InputFieldMapping {
  /** This node's input field key */
  input_field_key: string;
  /**
   * Upstream node key.
   * Use '_literal_' for fixed/manual values.
   */
  source_node_key: string;
  /** Upstream node's output field key */
  source_output_key: string;
  /**
   * Optional key path for extracting nested values from object-type outputs.
   * Supports dot notation: "user_name", "preferences.theme", etc.
   * If not specified, the entire output value is used.
   */
  source_key_path?: string;
  /**
   * Literal/fixed value for manual input.
   * Only used when source_node_key is '_literal_'.
   */
  literal_value?: string;
  /** Whether this was auto-mapped by the system */
  is_auto_mapped: boolean;
}

/**
 * Check if a mapping is a literal/fixed value
 */
export function isLiteralMapping(mapping: InputFieldMapping): boolean {
  return mapping.source_node_key === LITERAL_SOURCE_KEY;
}

/**
 * Complete field configuration for a node
 */
export interface NodeFieldsConfig {
  inputs: FieldDefinition[];
  outputs: OutputFieldDefinition[];
  input_mappings: InputFieldMapping[];
}

/**
 * Condition for showing a config field
 */
export interface ConfigFieldShowWhen {
  /** Field key to check */
  field: string;
  /** Value(s) to match (string, array, or boolean) */
  value: string | string[] | boolean;
}

/**
 * Definition of a config option field for UI rendering.
 *
 * Config fields are distinct from input/output fields - they define
 * the node's configuration UI, not data flow connections.
 */
export interface ConfigFieldDefinition {
  /** Field key in node.data (e.g., 'input_mode', 'model') */
  key: string;
  /** Display label for UI */
  label: string;
  /** UI control type */
  type: ConfigFieldType;
  /** Whether this field is required */
  required?: boolean;
  /** Help text for the field */
  description?: string;
  /** Default value */
  default?: unknown;

  /** Static options for select/radio fields [{value, label}] */
  options?: Array<{ value: string; label: string }>;
  /** Dynamic data source: agents, chatModels, imageModels, outputSchemas, etc. */
  options_source?: string;

  /** Show field only when condition is met */
  show_when?: ConfigFieldShowWhen;

  /** Whether field supports mapping from input variables */
  mapping_enabled?: boolean;
  /** Compatible FieldTypes for mapping */
  compatible_types?: FieldType[];

  /** Custom renderer name: builtin-variables, model-config, etc. */
  renderer?: string;
}

/**
 * Node type specification - defines the I/O contract for a node type
 */
export interface NodeTypeSpec {
  /** Unique node type identifier */
  node_type: string;
  /** Display label for UI */
  label: string;
  /** Description of what the node does */
  description: string;
  /** Default input field definitions */
  default_inputs: FieldDefinition[];
  /** Default output field definitions */
  default_outputs: OutputFieldDefinition[];
  /** Config options for UI rendering (declarative) */
  config_fields?: ConfigFieldDefinition[];
  /** Whether this node supports indexed inputs */
  supports_indexed_inputs?: boolean;
  /** Maximum number of inputs (for indexed) */
  max_inputs?: number;
  /** Whether custom output fields can be added */
  allows_custom_outputs?: boolean;
  /** Node category for UI grouping */
  category?: string;
}

/**
 * Output field info within an available source (for mapping UI)
 */
export interface AvailableSourceOutput {
  key: string;
  type: FieldType;
  label?: string;
  /** For object-type outputs: available sub-keys for extraction */
  object_keys?: ObjectKeyInfo[];
}

/**
 * Available source node with its output fields (for mapping UI)
 */
export interface AvailableSource {
  node_key: string;
  node_type: string;
  node_label: string;
  outputs: AvailableSourceOutput[];
}

/**
 * Context variable definition (from Start node's enabled builtins)
 */
export interface ContextVariable {
  key: string;
  label: string;
  type: FieldType;
}

/**
 * Start node custom variable
 */
export interface StartVariable {
  key: string;
  value: string;
}

/**
 * Custom input field (for FieldSourceToggle)
 */
export interface CustomInputField {
  key: string;
  type: FieldType;
  label?: string;
  object_keys?: ObjectKeyInfo[];
}

/**
 * Node type registry response from API
 */
export interface NodeTypeRegistryResponse {
  node_types: NodeTypeSpec[];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get field type display label
 */
export function getFieldTypeLabel(type: FieldType): string {
  const labels: Record<FieldType, string> = {
    // Metadata types
    string: 'String',
    number: 'Number',
    boolean: 'Boolean',
    array: 'Array',
    object: 'Object',
    // Media types
    image: 'Image',
    audio: 'Audio',
    video: 'Video',
    document: 'Document',
    // Legacy
    image_url: 'Image URL (legacy)',
  };
  return labels[type] || type;
}

/**
 * Get field type color for UI
 */
export function getFieldTypeColor(type: FieldType): string {
  const colors: Record<FieldType, string> = {
    // Metadata types
    string: '#4CAF50',     // Green
    number: '#2196F3',     // Blue
    boolean: '#FF9800',    // Orange
    array: '#9C27B0',      // Purple
    object: '#795548',     // Brown
    // Media types (all pink/magenta shades)
    image: '#E91E63',      // Pink
    audio: '#9C27B0',      // Purple
    video: '#673AB7',      // Deep Purple
    document: '#3F51B5',   // Indigo
    // Legacy
    image_url: '#E91E63',  // Pink
  };
  return colors[type] || '#757575';
}

/**
 * Check if two field types are compatible for mapping
 */
export function areFieldTypesCompatible(
  sourceType: FieldType,
  targetType: FieldType
): boolean {
  // Exact match
  if (sourceType === targetType) return true;

  // Legacy: image_url is compatible with image
  if (sourceType === 'image_url' && targetType === 'image') return true;
  if (sourceType === 'image' && targetType === 'image_url') return true;

  // Media types are compatible with string (file paths are strings)
  const mediaTypes: FieldType[] = ['image', 'audio', 'video', 'document', 'image_url'];
  if (targetType === 'string' && mediaTypes.includes(sourceType)) {
    return true;
  }
  if (mediaTypes.includes(targetType) && sourceType === 'string') {
    return true;
  }

  // String can receive most primitive types
  if (targetType === 'string') {
    return ['string', 'number', 'boolean'].includes(sourceType);
  }

  return false;
}

/**
 * Create default field definition
 */
export function createDefaultFieldDefinition(
  key: string,
  type: FieldType = 'string'
): FieldDefinition {
  return {
    key,
    type,
    required: true,
    indexed: false,
  };
}

/**
 * Create default output field definition
 */
export function createDefaultOutputFieldDefinition(
  key: string,
  type: FieldType = 'string'
): OutputFieldDefinition {
  return {
    key,
    type,
    required: true,
    indexed: false,
  };
}

/**
 * Create default input field mapping
 */
export function createDefaultInputFieldMapping(
  inputFieldKey: string,
  sourceNodeKey: string,
  sourceOutputKey: string,
  isAutoMapped = true
): InputFieldMapping {
  return {
    input_field_key: inputFieldKey,
    source_node_key: sourceNodeKey,
    source_output_key: sourceOutputKey,
    is_auto_mapped: isAutoMapped,
  };
}
